import React, {useEffect, useState} from 'react';
import {Typography, Button, TextField, Table, TableHead, TableBody, TableRow, TableCell, Paper, CircularProgress, TableSortLabel, TablePagination, IconButton, Switch, Tooltip} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FunctionsData from '../../../Functions/FunctionsData';
import MicrochipOrderDialog from './MicrochipOrderDialog';

export default function StaffMicrochips() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [orders, setOrders] = useState({});
    const [sending, setSending] = useState({});
    const [err, setErr] = useState('');
    const [orderDialog, setOrderDialog] = useState({ open: false, clinic: null, quantity: 0 });
    const [supplierEmail, setSupplierEmail] = useState('snipnchip@spca.nz');
    const [emailTemplate, setEmailTemplate] = useState(
        `Hi there,\nCan we please order the following:\n[amount] microchips for [vet clinic name] at [address], Attn [contact name] (SNC [vet number]).\nPlease send confirmation to snipnchip@spca.nz\nThanks so much.\nSPCA Desexing Team`
    );
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('microchipsSupplied');
    const [sortDir, setSortDir] = useState('asc');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const sameClinic = (a, b) => String(a) === String(b);

    const hasOutstandingRequest = (row) => {
        const requested = Number(row?.microchipsRequested);
        return Number.isFinite(requested) && requested > 0;
    };

    // Modal dialog state for editing fields
    const [editEmailOpen, setEditEmailOpen] = useState(false);
    const [editEmailValue, setEditEmailValue] = useState(supplierEmail);
    const [editTemplateOpen, setEditTemplateOpen] = useState(false);
    const [editTemplateValue, setEditTemplateValue] = useState(emailTemplate);
    const [editSuppliedOpen, setEditSuppliedOpen] = useState(false);
    const [editSuppliedValue, setEditSuppliedValue] = useState(0);
    const [editSuppliedClinicId, setEditSuppliedClinicId] = useState(null);

    function loadData() {
        setLoading(true);
        FunctionsData.fetchDataGet(
            FunctionsData.getApiUrl('/api/vet/microchips/summary'),
            (data) => {
                const rows = (data || []).map(row => ({
                    ...row,
                    pending: row.pending ?? (Number(row.microchipsRequested) > 0),
                    sent: row.sent ?? false,
                }));
                setRows(rows);
            },
            true,
            setLoading
        );
    }

    useEffect(() => {
        loadData();
    }, []);

    function handleQtyChange(clinicId, value) {
        setOrders(prev => ({...prev, [clinicId]: value}));
    }

    function placeOrder(clinicId) {
        const qty = parseInt(orders[clinicId], 10) || 0;
        if (qty <= 0) {
            alert('Please enter a quantity greater than zero');
            return;
        }
        const clinic = rows.find(r => sameClinic(r.clinicId, clinicId));
        if (!clinic) {
            alert('Clinic not found for this order. Please refresh and try again.');
            return;
        }
        setOrderDialog({ open: true, clinic, quantity: qty });
    }

    function handleOrderDialogClose() {
        setOrderDialog({ open: false, clinic: null, quantity: 0 });
    }

    function handleOrderDialogConfirm({ email, content }) {
        setSending(prev => ({ ...prev, [orderDialog.clinic.clinicId]: true }));
        FunctionsData.postData(
            FunctionsData.getApiUrl('/api/vet/microchips/order'),
            {
                clinicId: orderDialog.clinic.clinicId,
                quantity: orderDialog.quantity,
                email,
                content,
                subject: 'Microchip Order Request from SPCA Desexing'
            }
        )
            .then((res) => {
                if (res && res.success) {
                    alert('Order placed — supplier has been notified');
                    // mark request on staff list: increase requested, mark pending and allow 'Sent' to be clicked
                    setRows(prev => prev.map(row => sameClinic(row.clinicId, orderDialog.clinic.clinicId) ? { ...row, microchipsRequested: (Number(row.microchipsRequested) || 0) + orderDialog.quantity, pending: true, sent: false, lastRequestedAt: (new Date()).toISOString() } : row));
                    setOrders(prev => ({ ...prev, [orderDialog.clinic.clinicId]: '' }));
                } else {
                    alert('Failed to place order: ' + ((res && res.message) || 'unknown error'));
                }
            })
            .catch(e => {
                console.error(e);
                alert('Failed to place order');
            })
            .finally(() => {
                setSending(prev => ({ ...prev, [orderDialog.clinic.clinicId]: false }));
                handleOrderDialogClose();
            });
    }

    function handleDelayedToggle(clinicId, value) {
        // backend expects numeric flag; send 1/0 to avoid truthy/falsey mismatch
        FunctionsData.postData(
            FunctionsData.getApiUrl('/api/vet/microchips/delay'),
            { clinicId, delayed: value ? 1 : 0 }
        ).then(() => {
            setRows(prev => prev.map(row => sameClinic(row.clinicId, clinicId) ? { ...row, delayed: !!value } : row));
        });
    }

    function handleSent(clinicId) {
        FunctionsData.postData(
            FunctionsData.getApiUrl('/api/vet/microchips/sent'),
            { clinicId }
        ).then((res) => {
            if (res && res.success === false) {
                alert('Failed to mark as sent: ' + (res.message || 'unknown error'));
                return;
            }
            const now = (new Date()).toISOString();
            setRows(prev => prev.map(row => sameClinic(row.clinicId, clinicId)
                ? { ...row, microchipsSupplied: (Number(row.microchipsSupplied) || 0) + (Number(row.microchipsRequested) || 0), microchipsRequested: 0, sent: true, pending: false, sentDate: now }
                : row));
            // Re-fetch to ensure data consistency with backend
            loadData();
        });
    }

    function handleSuppliedEdit(clinicId) {
        const current = rows.find(r => sameClinic(r.clinicId, clinicId))?.microchipsSupplied || 0;
        setEditSuppliedValue(current);
        setEditSuppliedClinicId(clinicId);
        setEditSuppliedOpen(true);
    }

    function handleEditSuppliedSave() {
        if (!isNaN(parseInt(editSuppliedValue, 10))) {
            FunctionsData.postData(
                FunctionsData.getApiUrl('/api/vet/microchips/supplied'),
                { clinicId: editSuppliedClinicId, supplied: parseInt(editSuppliedValue, 10) }
            ).then(() => {
                setRows(prev => prev.map(row => sameClinic(row.clinicId, editSuppliedClinicId) ? { ...row, microchipsSupplied: parseInt(editSuppliedValue, 10) } : row));
                setEditSuppliedOpen(false);
                // Re-fetch to ensure data consistency with backend
                loadData();
            });
        }
    }

    function handleEditEmailSave() {
        setSupplierEmail(editEmailValue);
        setEditEmailOpen(false);
    }

    function handleEditTemplateSave() {
        setEmailTemplate(editTemplateValue);
        setEditTemplateOpen(false);
    }

    const columnSort = (a, b) => {
        let vA = a[sortBy];
        let vB = b[sortBy];
        const numA = Number(vA);
        const numB = Number(vB);
        const isNumA = Number.isFinite(numA);
        const isNumB = Number.isFinite(numB);
        if (isNumA && isNumB) {
            if (numA < numB) return sortDir === 'asc' ? -1 : 1;
            if (numA > numB) return sortDir === 'asc' ? 1 : -1;
            return 0;
        }
        const sA = (vA ?? '').toString().toLowerCase();
        const sB = (vB ?? '').toString().toLowerCase();
        if (sA < sB) return sortDir === 'asc' ? -1 : 1;
        if (sA > sB) return sortDir === 'asc' ? 1 : -1;
        return 0;
    };
    let filteredRows = rows.filter(row =>
        row.clinicName?.toLowerCase().includes(search.toLowerCase())
        || row.supplier_address?.toLowerCase().includes(search.toLowerCase())
    );
    // Sort within each priority group so new clinics always appear first
    const groupNew = filteredRows.filter(r => r.newClinic).sort(columnSort);
    const groupPending = filteredRows.filter(r => r.pending && !r.newClinic).sort(columnSort);
    const groupRest = filteredRows.filter(r => !r.newClinic && !r.pending).sort(columnSort);
    filteredRows = [...groupNew, ...groupPending, ...groupRest];
    const pagedRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    if (loading) return <div style={{padding:20}}><CircularProgress/></div>;

    return (
        <Paper style={{padding:20}}>
            <Typography variant="h5" gutterBottom>
                Microchips
            </Typography>
            <Typography variant="body2" style={{marginBottom:10}}>Monitor microchip requests and place orders to supplier.</Typography>
            {err ? <Typography color="error">{err}</Typography> : null}

            <div style={{display:'flex', alignItems:'center', marginBottom:16, gap:8}}>
                <TextField
                    label="Search clinics"
                    variant="outlined"
                    size="small"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{maxWidth:300}}
                />
                <Button variant="outlined" onClick={() => {
                    setEditEmailValue(supplierEmail);
                    setEditEmailOpen(true);
                }}>Edit Staff Member Email</Button>
                <Button variant="outlined" onClick={() => {
                    setEditTemplateValue(emailTemplate);
                    setEditTemplateOpen(true);
                }}>Edit Email Template</Button>
            </div>

            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <TableSortLabel
                                active={sortBy === 'clinicName'}
                                direction={sortDir}
                                onClick={() => {
                                    setSortBy('clinicName');
                                    setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
                                }}
                            >
                                Clinic
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>Address</TableCell>
                        <TableCell align="right">
                            <TableSortLabel
                                active={sortBy === 'microchipsSupplied'}
                                direction={sortDir}
                                onClick={() => {
                                    setSortBy('microchipsSupplied');
                                    setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
                                }}
                            >
                                Microchips supplied
                            </TableSortLabel>
                        </TableCell>
                        <TableCell align="right">
                            <TableSortLabel
                                active={sortBy === 'microchipsRequested'}
                                direction={sortDir}
                                onClick={() => {
                                    setSortBy('microchipsRequested');
                                    setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
                                }}
                            >
                                Microchips requested
                            </TableSortLabel>
                        </TableCell>
                        <TableCell align="right">Vouchers redeemed</TableCell>
                        <TableCell align="center">Delayed</TableCell>
                        <TableCell align="center">Sent</TableCell>
                        <TableCell align="right">Order new microchips</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {pagedRows.map(row => (
                        <TableRow key={row.clinicId} style={row.newClinic ? {background:'#e8f5e9'} : row.pending ? {background:'#fffde7'} : {}}>
                            <TableCell>
                                {row.clinicName}
                                {row.newClinic && <NewReleasesIcon style={{color:'green', marginLeft:4}} fontSize="small" />}
                                {row.pending && <AccessTimeIcon style={{color:'#ff9800', marginLeft:4}} fontSize="small" />}
                            </TableCell>
                            <TableCell>{row.supplier_address}</TableCell>
                            <TableCell align="right">
                                {row.microchipsSupplied ?? 0}
                                <IconButton size="small" onClick={() => handleSuppliedEdit(row.clinicId)}>
                                    <EditIcon fontSize="small"/>
                                </IconButton>
                            </TableCell>
                            <TableCell align="right">{row.microchipsRequested ?? 0}</TableCell>
                            <TableCell align="right">{row.vouchersRedeemed ?? 0}</TableCell>
                            <TableCell align="center">
                                <Switch
                                    checked={!!row.delayed}
                                    onChange={e => handleDelayedToggle(row.clinicId, e.target.checked)}
                                    color="warning"
                                />
                                {row.delayed && <WarningAmberIcon style={{color:'#ff9800', marginLeft:4}} fontSize="small" />}
                            </TableCell>
                            <TableCell align="center">
                                <Button
                                    variant="outlined"
                                    color="success"
                                    size="small"
                                    disabled={row.sent || !hasOutstandingRequest(row) || !row.pending}
                                    onClick={() => handleSent(row.clinicId)}
                                >
                                    {row.sent ? <CheckCircleIcon fontSize="small"/> : 'Sent'}
                                </Button>
                            </TableCell>
                            <TableCell align="right">
                                <div style={{display:'flex', justifyContent:'flex-end', gap:8}}>
                                    <TextField size="small" type="number" value={orders[row.clinicId] ?? ''} onChange={(e) => handleQtyChange(row.clinicId, e.target.value)} inputProps={{min:0}} />
                                    <Button variant="contained" onClick={() => placeOrder(row.clinicId)} disabled={sending[row.clinicId]}> {sending[row.clinicId] ? 'Ordering...' : 'Order'}</Button>
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <TablePagination
                component="div"
                count={filteredRows.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50]}
            />
            <MicrochipOrderDialog
                open={orderDialog.open}
                onClose={handleOrderDialogClose}
                onConfirm={handleOrderDialogConfirm}
                defaultEmail={supplierEmail}
                defaultTemplate={emailTemplate.replace('[address]', orderDialog.clinic?.supplier_address || '')}
                clinic={orderDialog.clinic}
                quantity={orderDialog.quantity}
            />
            {/* Edit Supplier Email Dialog */}
            <Dialog open={editEmailOpen} onClose={() => setEditEmailOpen(false)}>
                <DialogTitle>Edit Staff Member Placing Order</DialogTitle>
                <DialogContent>
                    <TextField 
                        fullWidth 
                        value={editEmailValue} 
                        onChange={e => setEditEmailValue(e.target.value)} 
                        label="Staff Member Placing Order" 
                        variant="outlined"
                        margin="dense"
                        InputLabelProps={{
                            shrink: true,
                            style: { background: '#fff', padding: '0 8px', color: '#222', fontWeight: 600, fontSize: 14, transform: 'translate(12px, -10px)' }
                        }}
                        sx={{ '& .MuiInputBase-input': { padding: '14px 14px' } }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditEmailOpen(false)}>Cancel</Button>
                    <Button onClick={handleEditEmailSave} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>
            {/* Edit Email Template Dialog */}
            <Dialog open={editTemplateOpen} onClose={() => setEditTemplateOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Edit Email Template</DialogTitle>
                <DialogContent>
                    <TextField 
                        fullWidth 
                        multiline 
                        minRows={6} 
                        value={editTemplateValue} 
                        onChange={e => setEditTemplateValue(e.target.value)} 
                        label="Email Template" 
                        variant="outlined"
                        margin="dense"
                        InputLabelProps={{
                            shrink: true,
                            style: { background: '#fff', padding: '0 8px', color: '#222', fontWeight: 600, fontSize: 14, transform: 'translate(12px, -10px)' }
                        }}
                        sx={{ '& .MuiInputBase-input': { padding: '14px 14px', whiteSpace: 'pre-wrap' } }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditTemplateOpen(false)}>Cancel</Button>
                    <Button onClick={handleEditTemplateSave} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>
            {/* Edit Microchips Supplied Dialog */}
            <Dialog open={editSuppliedOpen} onClose={() => setEditSuppliedOpen(false)}>
                <DialogTitle>Edit Microchips Supplied</DialogTitle>
                <DialogContent>
                    <TextField 
                        fullWidth 
                        type="number" 
                        value={editSuppliedValue} 
                        onChange={e => setEditSuppliedValue(e.target.value)} 
                        label="Microchips Supplied" 
                        variant="outlined"
                        margin="dense"
                        InputLabelProps={{
                            shrink: true,
                            style: { background: '#fff', padding: '0 8px', color: '#222', fontWeight: 600, fontSize: 14, transform: 'translate(12px, -10px)' }
                        }}
                        sx={{ '& .MuiInputBase-input': { padding: '14px 14px' } }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditSuppliedOpen(false)}>Cancel</Button>
                    <Button onClick={handleEditSuppliedSave} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}
